// v1.0.9 — Movie Cue (playlist / queue) library.
//
// A user-curated list of movies to be played back-to-back with an
// optional cinematic intermission between titles. Persisted in
// localStorage so it survives app restarts. Kept intentionally simple:
// no server round-trip, no schema evolution, no ordering UI beyond
// "append when added, drop when watched".
//
// API mirrors the favorites.js module for consistency.
import { getActiveProfileId } from "./profiles";

const BASE_KEY = "vupp_movie_cue_v1";
const SETTINGS_BASE_KEY = "vupp_movie_cue_settings_v1";
const DEFAULT_INTERMISSION_MIN = 10;
const ALLOWED_INTERMISSION_MIN = [5, 10, 15];

function cueKey() {
  const pid = getActiveProfileId();
  return pid ? `${BASE_KEY}__${pid}` : BASE_KEY;
}

function settingsKey() {
  const pid = getActiveProfileId();
  return pid ? `${SETTINGS_BASE_KEY}__${pid}` : SETTINGS_BASE_KEY;
}

function _read() {
  try {
    const raw = localStorage.getItem(cueKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function _write(list) {
  try {
    localStorage.setItem(cueKey(), JSON.stringify(list));
    // Fire an event so tile grids / sidebar counters can react without
    // a hard reload.
    try {
      window.dispatchEvent(new CustomEvent("movie-cue-changed"));
    } catch { /* noop */ }
  } catch { /* quota, ignore */ }
}

/** Return the current cue as an array of movie items (in play order). */
export function getCue() {
  return _read();
}

/** Number of movies currently queued. */
export function cueCount() {
  return _read().length;
}

/** True if the given stream_url is already in the cue. */
export function isInCue(streamUrl) {
  if (!streamUrl) return false;
  return _read().some((m) => m.stream_url === streamUrl);
}

/**
 * Add a movie to the end of the cue.  Idempotent — a movie is only
 * ever in the cue once. Returns the resulting list length.
 */
export function addToCue(movie) {
  if (!movie || !movie.stream_url) return _read().length;
  const list = _read();
  if (list.some((m) => m.stream_url === movie.stream_url)) return list.length;
  // Only persist the fields we need for playback / display — avoids
  // bloating localStorage with EPG blobs.
  const trimmed = {
    stream_url: movie.stream_url,
    name: movie.name,
    logo_url: movie.logo_url,
    year: movie.year,
    group: movie.group,
    content_type: "movie",
    added_at: new Date().toISOString(),
  };
  list.push(trimmed);
  _write(list);
  return list.length;
}

/** Remove one movie from the cue by stream_url. Returns the new list. */
export function removeFromCue(streamUrl) {
  const list = _read().filter((m) => m.stream_url !== streamUrl);
  _write(list);
  return list;
}

/**
 * Toggle. Convenience wrapper for the tile overlay button.  Returns
 * true if the movie is now in the cue after the toggle, false if it
 * was removed.
 */
export function toggleCue(movie) {
  if (!movie || !movie.stream_url) return false;
  if (isInCue(movie.stream_url)) {
    removeFromCue(movie.stream_url);
    return false;
  }
  addToCue(movie);
  return true;
}

/** Reorder / replace the entire cue. Used by the "Play all" flow to
 * advance the head as movies finish. */
export function setCue(list) {
  _write(Array.isArray(list) ? list : []);
}

/** Wipe the whole cue. Not exposed in the UI yet — bind to a menu
 * item if needed. */
export function clearCue() {
  _write([]);
}

// ----- Settings ---------------------------------------------------
/**
 * Intermission length between two consecutive movies in the "Play
 * all" flow.  Constrained to {5, 10, 15} minutes so the countdown UI
 * doesn't have to handle arbitrary values.
 */
export function getIntermissionMinutes() {
  try {
    const raw = localStorage.getItem(settingsKey());
    if (!raw) return DEFAULT_INTERMISSION_MIN;
    const parsed = JSON.parse(raw);
    const n = Number(parsed?.intermission_min);
    return ALLOWED_INTERMISSION_MIN.includes(n) ? n : DEFAULT_INTERMISSION_MIN;
  } catch {
    return DEFAULT_INTERMISSION_MIN;
  }
}

export function setIntermissionMinutes(minutes) {
  const n = Number(minutes);
  const clean = ALLOWED_INTERMISSION_MIN.includes(n) ? n : DEFAULT_INTERMISSION_MIN;
  try {
    localStorage.setItem(
      settingsKey(),
      JSON.stringify({ intermission_min: clean }),
    );
  } catch { /* quota, ignore */ }
  return clean;
}

export const INTERMISSION_OPTIONS = ALLOWED_INTERMISSION_MIN;
